import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, Share, MoreVertical, Download, CheckCircle2, ExternalLink, Copy, Monitor, Zap, Bell, WifiOff, Camera } from "lucide-react";
import { toast } from "sonner";
import { detectPlatform, getDeferredPrompt, triggerInstall, openInChromeIntent } from "@/lib/install";

const PERKS = [
  { icon: Zap, t: "Opens instantly", d: "Full-screen, no browser bars — straight from your home screen." },
  { icon: Bell, t: "Enquiry & sale alerts", d: "Buyers message you? You know in seconds." },
  { icon: Camera, t: "Go live from your phone", d: "Host your Stage with your camera, one tap." },
  { icon: WifiOff, t: "Light on data", d: "Loads what you've seen before even on a weak connection." },
];

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="h-7 w-7 shrink-0 rounded-full bg-white text-black grid place-items-center text-xs font-black">{n}</span>
      <span className="text-sm text-neutral-200 leading-relaxed pt-1">{children}</span>
    </li>
  );
}

export default function GetApp() {
  const [p] = useState(() => detectPlatform());
  const [ready, setReady] = useState(!!getDeferredPrompt());
  const [installed, setInstalled] = useState(p.standalone || localStorage.getItem("vismarks_app_installed") === "1");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const on = () => setReady(true);
    const done = () => { setInstalled(true); setReady(false); };
    window.addEventListener("vsk-install-ready", on);
    window.addEventListener("vsk-install-done", done);
    return () => { window.removeEventListener("vsk-install-ready", on); window.removeEventListener("vsk-install-done", done); };
  }, []);

  const install = async () => {
    setBusy(true);
    const outcome = await triggerInstall();
    setBusy(false);
    if (outcome === "accepted") toast.success("VISMARKS is installing — check your home screen");
    else if (outcome === "dismissed") toast("No problem — you can install any time from this page");
    else toast.error("Your browser didn't offer install here — follow the steps below");
    if (outcome !== "accepted") setReady(false);
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/app`); toast.success("Link copied — paste it in Chrome or Safari"); }
    catch { toast.error("Copy failed — the link is vismarks.com/app"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 sm:py-16" data-testid="get-app-page">
      <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF3B30]/40 bg-[#FF3B30]/10">
            <Smartphone className="h-3.5 w-3.5 text-[#FF3B30]" />
            <span className="text-xs font-semibold text-[#FF3B30] uppercase tracking-widest">The VISMARKS app</span>
          </div>
          <h1 className="mt-4 font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl">Put the whole theatre in your pocket<span className="text-[#FF3B30]">.</span></h1>
          <p className="mt-4 text-neutral-400 leading-relaxed max-w-xl">VISMARKS installs straight from your browser — no app store, no 100 MB download, always the latest version. It takes about ten seconds.</p>

          <div className="mt-8 rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid="install-panel">
            {installed ? (
              <div className="flex items-center gap-3" data-testid="install-installed">
                <CheckCircle2 className="h-6 w-6 text-[#34C759]" />
                <div><div className="font-black text-white">VISMARKS is installed on this device</div><div className="text-sm text-neutral-400">Open it from your home screen or app drawer.</div></div>
              </div>
            ) : ready ? (
              <div data-testid="install-ready">
                <div className="font-black text-white text-lg">Ready to install</div>
                <p className="text-sm text-neutral-400 mt-1">Your browser supports one-tap install.</p>
                <button onClick={install} disabled={busy} data-testid="install-app-btn" className="mt-4 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#FF3B30] hover:bg-[#e0342a] text-white font-bold disabled:opacity-60">
                  <Download className="h-4 w-4" /> {busy ? "Opening…" : "Install VISMARKS"}
                </button>
              </div>
            ) : p.inApp ? (
              <div data-testid="install-inapp">
                <div className="font-black text-white text-lg">Open in your real browser first</div>
                <p className="text-sm text-neutral-400 mt-1">You're inside {/whatsapp/i.test(navigator.userAgent) ? "WhatsApp" : /instagram/i.test(navigator.userAgent) ? "Instagram" : /fb/i.test(navigator.userAgent) ? "Facebook" : "an in-app browser"}, which can't install apps. Tap the <MoreVertical className="h-3.5 w-3.5 inline" /> menu and choose <span className="text-white font-semibold">"Open in {p.ios ? "Safari" : "Chrome"}"</span>, or:</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {p.android && <a href={openInChromeIntent("/app")} data-testid="open-in-chrome-btn" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-black font-bold text-sm"><ExternalLink className="h-4 w-4" /> Open in Chrome</a>}
                  <button onClick={copyLink} data-testid="copy-link-btn" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/20 text-white font-bold text-sm"><Copy className="h-4 w-4" /> Copy link</button>
                </div>
              </div>
            ) : p.ios ? (
              <div data-testid="install-ios">
                <div className="font-black text-white text-lg">Install on iPhone or iPad</div>
                {!p.safari && <p className="mt-1 text-sm text-[#FF9F0A]">Open this page in <span className="font-semibold">Safari</span> first — iOS only installs apps from Safari.</p>}
                <ol className="mt-4 space-y-3">
                  <Step n={1}>Tap the <Share className="h-4 w-4 inline text-[#007AFF] -mt-0.5" /> <span className="text-white font-semibold">Share</span> button at the bottom of Safari.</Step>
                  <Step n={2}>Scroll down and tap <span className="text-white font-semibold">"Add to Home Screen"</span>.</Step>
                  <Step n={3}>Tap <span className="text-white font-semibold">Add</span>. VISMARKS appears on your home screen like any app.</Step>
                </ol>
              </div>
            ) : p.android ? (
              <div data-testid="install-android">
                <div className="font-black text-white text-lg">Install on Android</div>
                <ol className="mt-4 space-y-3">
                  <Step n={1}>Tap the <MoreVertical className="h-4 w-4 inline -mt-0.5" /> menu at the top-right of {p.samsung ? "Samsung Internet" : p.firefox ? "Firefox" : "Chrome"}.</Step>
                  <Step n={2}>Tap <span className="text-white font-semibold">{p.samsung ? "\"Add page to\" → \"Home screen\"" : p.firefox ? "\"Install\"" : "\"Install app\" or \"Add to Home screen\""}</span>.</Step>
                  <Step n={3}>Confirm. VISMARKS appears on your home screen.</Step>
                </ol>
                {p.chrome && <p className="mt-4 text-xs text-neutral-500">If you dismissed Chrome's install banner before, it hides it for a while — the menu route above always works.</p>}
              </div>
            ) : (
              <div data-testid="install-desktop">
                <div className="font-black text-white text-lg">Install on this computer</div>
                <ol className="mt-4 space-y-3">
                  <Step n={1}>In Chrome or Edge, look for the <Monitor className="h-4 w-4 inline -mt-0.5" /> <span className="text-white font-semibold">install icon</span> at the right end of the address bar.</Step>
                  <Step n={2}>Click it and choose <span className="text-white font-semibold">Install</span>. VISMARKS opens in its own window.</Step>
                </ol>
                <p className="mt-4 text-xs text-neutral-500">On your phone? Open <span className="text-neutral-300 font-mono">vismarks.com/app</span> there for one-tap install.</p>
              </div>
            )}
          </div>
          <p className="mt-4 text-xs text-neutral-500">Already using the site? Nothing changes — same account, same Stage. <Link to="/" className="underline hover:text-neutral-300">Back to the feed</Link>.</p>
        </div>

        <div className="space-y-3 lg:pt-16">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#1a0b0b] via-[#121212] to-black p-6 flex items-center gap-4">
            <img src="/icon-192.png" alt="VISMARKS app icon" className="h-20 w-20 rounded-2xl shadow-2xl" />
            <div><div className="font-black text-white text-xl tracking-tight">VISMARKS</div><div className="text-sm text-neutral-400">A Million Products. Always Live.</div><div className="mt-1 text-xs text-neutral-500">Free · 0 MB download · updates itself</div></div>
          </div>
          {PERKS.map(({ icon: Icon, t, d }) => (
            <div key={t} className="rounded-xl border border-white/10 bg-[#121212] p-4 flex items-start gap-3">
              <Icon className="h-5 w-5 text-[#FF9F0A] shrink-0 mt-0.5" />
              <div><div className="font-bold text-white text-sm">{t}</div><div className="text-xs text-neutral-400 mt-0.5">{d}</div></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
